"use client";

import { usePrevious } from "@faire/react/hooks/usePrevious";
import { Dimensions } from "@faire/slate/foundation/dimensions/primitives";
import { SVGIconProps } from "@faire/slate-icons/types";
import { buildId } from "@faire/slate/lib/idUtils";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { useControllableState } from "@faire/slate/private/hooks/useControllableState";
import { RadioBase } from "@faire/slate/private/radio/RadioBase";
import { IRadioWebProps } from "@faire/slate/private/radio/types";
import {
  SELECTION_CARD_DESCRIPTION_ID_PREFIX,
  SELECTION_CARD_LABEL_ID_PREFIX,
} from "@faire/slate/private/selection-card/consts";
import type { IText } from "@faire/slate/private/shared/types";
import React, { useEffect, useRef } from "react";

type IProps = {
  /**
   * The selection card bottom slot.
   */
  bottomSlot?: React.ReactNode;

  /**
   * Whether the selection card is checked.
   */
  checked?: boolean;

  /**
   * The selection card description.
   */
  description?: IText;

  /**
   * The selection card end slot.
   */
  endSlot?: React.ReactNode;

  /**
   * The selection card inline slot.
   */
  inlineSlot?: React.ReactNode;

  /**
   * The selection card label.
   */
  label: IText;

  /**
   * The icon component to render
   */
  Icon?: React.FC<SVGIconProps>;

  /**
   * Called when the card is selected.
   *
   * @warning With `getSelectionCardGroupProps`, pass this in its options
   * instead: `getSelectionCardGroupProps(name, { onChange })`. An `onChange`
   * after the spread replaces the form's handler, so the value never updates.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  // DO NOT ADD any new prop without aligning with Slate team first
} & Omit<IRadioWebProps, "onChange">;

/**
 * Selection Cards enable users to select an option similar to a radio button with additional styling.
 *
 * {@link https://www.notion.so/faire/Selection-Card-2242efb5c25a8048a616c00d27292b19 | Slate/SelectionCard}
 */
export const SelectionCard: React.FC<IProps> = ({
  bottomSlot,
  description,
  endSlot,
  id,
  inlineSlot,
  label,
  checked: checkedProp,
  onChange,
  Icon,
  ...props
}) => {
  const labelId = buildId(SELECTION_CARD_LABEL_ID_PREFIX, id, label);
  const descriptionId = buildId(
    SELECTION_CARD_DESCRIPTION_ID_PREFIX,
    id,
    label
  );

  const [checked, setChecked] = useControllableState({
    initialValue: false,
    value: checkedProp,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked);
    onChange?.(e);
  };

  // NOTE: because the element with htmlFor changes when element is checked, the radio input loses focus, so we need to re-focus it
  const inputRef = useRef<HTMLInputElement>(null);
  const previousChecked = usePrevious(checked);

  useEffect(() => {
    // Only focus if checked changed from false to true, not on initial mount
    // usePrevious returns undefined on first render, which prevents focusing on mount
    if (checked && previousChecked === false) {
      inputRef.current?.focus();
    }
  }, [checked, previousChecked]);

  // When checked, we allow seleciton card to have nested interactive elements
  // To be WCAG compliant we have to update the container tag
  const Container = checked ? "div" : "label";
  const Label = checked ? "label" : "span";

  return (
    <Container
      className={containerStyle.cls({ checked, className: componentCls() })}
      data-component-name="Slate/SelectionCard"
      htmlFor={checked ? undefined : id}
      id={checked ? undefined : labelId}
    >
      <RadioBase
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        {...props}
        id={id}
        hasLabel
        onChange={handleChange}
        checked={checked}
        ref={inputRef}
      />
      <div className="fs-selection-card__content">
        <div className="fs-selection-card__start-slot">
          <div className="fs-selection-card__label-container">
            {Icon ? (
              <div className="fs-selection-card__icon">
                <Icon
                  aria-hidden
                  color="inherit"
                  fontSize={Dimensions[6]}
                  role="presentation"
                  titleAccess={undefined}
                />
              </div>
            ) : null}
            <Label
              className="fs-selection-card__label"
              htmlFor={checked ? id : undefined}
              id={checked ? labelId : undefined}
            >
              {label}
            </Label>
            {inlineSlot ? <div aria-hidden>{inlineSlot}</div> : null}
          </div>
          {description ? (
            <p
              className="fs-selection-card__description"
              id={descriptionId}
              aria-hidden
            >
              {description}
            </p>
          ) : null}
        </div>
        {endSlot ? (
          <div className="fs-selection-card__end-slot" aria-hidden>
            {endSlot}
          </div>
        ) : null}
        {bottomSlot ? (
          <div className="fs-selection-card__bottom-slot" aria-hidden>
            {bottomSlot}
          </div>
        ) : null}
      </div>
    </Container>
  );
};

const containerStyle = tsv({
  base: "fs-selection-card grid grid-cols-[auto_1fr] gap-3",
  variants: {
    checked: {
      false: "interactive",
    },
  },
});
